<template>
  <div ref="createTareaRef" id="text-test">
    <v-dialog
      v-model="modalStatus"
      :max-width="$vuetify.breakpoint.mobile ? undefined : '780'"
      :fullscreen="$vuetify.breakpoint.mobile"
      scrollable
      persistent
    >
      <v-card class="modal-card" elevation="0">

        <!-- Header -->
        <div class="modal-header">
          <div class="modal-header-left">
            <div class="modal-icon">
              <v-icon color="white" size="16">mdi-file-document-multiple-outline</v-icon>
            </div>
            <div>
              <p class="modal-title">Nueva Entrega</p>
              <p class="modal-subtitle">
                <span>{{ $store.userStore.state.userInfo.name }}</span>
                <span class="separator">·</span>
                <span>{{ $store.projectsStore.state.selectedProject.name }}</span>
              </p>
            </div>
          </div>
          <div class="modal-header-actions">
            <button class="header-btn minimize" @click="minimizeForm" title="Minimizar">
              <v-icon size="14" color="white">mdi-window-minimize</v-icon>
            </button>
            <button class="header-btn close" @click="closeModalSelf()" title="Cerrar">
              <v-icon size="14" color="white">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <v-card-text class="modal-body pa-0">
          <v-form ref="form" v-model="validform">

            <!-- Nombre de la entrega -->
            <div class="form-section title-section">
              <v-text-field
                v-model="newEntregaForm.title"
                placeholder="Nombre de la entrega…"
                outlined
                required
                dense
                :rules="requiredRule"
                class="title-input"
              />
            </div>

            <!-- Protocolo -->
            <div class="form-section">
              <label class="field-label">Protocolo</label>
              <v-autocomplete
                v-model="newEntregaForm.protocolo"
                :items="protocolos"
                item-text="title"
                item-value="id"
                outlined
                dense
                :rules="requiredRule"
                required
                placeholder="Buscar protocolo…"
                prepend-inner-icon="mdi-magnify"
                no-data-text="Sin resultados"
                clearable
              />
            </div>

            <!-- Tabs -->
            <div class="tabs-bar">
              <button
                class="tab-btn"
                :class="{ active: tab === 0 }"
                type="button"
                @click="tab = 0"
              >CONFIGURACIÓN</button>
              <button
                class="tab-btn"
                :class="{ active: tab === 1 }"
                type="button"
                @click="tab = 1"
              >SUBTAREAS</button>
            </div>

            <!-- Tab: Configuración -->
            <div v-show="tab === 0">

              <!-- Emplazamiento -->
              <div class="form-section">
                <p class="section-title">Emplazamiento</p>
                <div class="plano-row">
                  <ControlPlanoModal
                    :planos="planos"
                    :plano-entrega="newEntregaForm.plano_entrega"
                    @save-plano="handlePlanoSaved"
                  />
                  <span v-if="!newEntregaForm.plano_entrega" class="plano-hint">Sin plano asociado</span>
                  <span v-if="planoError" class="plano-error error--text">El emplazamiento es obligatorio</span>
                </div>
              </div>

              <!-- Ubicación -->
              <div class="form-section">
                <p class="section-title">Ubicación</p>

                <!-- Zona -->
                <div class="field-row">
                  <span class="row-label">Zona</span>
                  <v-select
                    v-model="newEntregaForm.zona"
                    :items="zonasList"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    @change="handleZonaChange"
                    :rules="requiredRule"
                    required
                  >
                    <template v-slot:selection="{ item }">
                      <div class="d-flex align-center">
                        <v-icon color="#2D4B95" small class="mr-2">mdi-map-marker-radius</v-icon>
                        {{ item.name }}
                      </div>
                    </template>
                    <template v-slot:item="{ item }">
                      <div class="d-flex align-center">
                        <v-icon color="#2D4B95" small class="mr-2">mdi-map-marker-radius</v-icon>
                        {{ item.name }}
                      </div>
                    </template>
                  </v-select>
                </div>

                <!-- Ejes Alfabéticos -->
                <div class="field-block">
                  <span class="row-label">Ejes Alfabéticos</span>
                  <div class="two-col">
                    <v-select
                      v-model="newEntregaForm.ejesAlfabeticos"
                      :items="getConfigOptions('ejesAlfabeticos')"
                      item-text="name"
                      item-value="id"
                      outlined
                      dense
                      :error="attemptedSubmit && !ejesValid"
                      :rules="requiredRule"
                      required
                      placeholder="Desde"
                    />
                    <v-select
                      v-model="newEntregaForm.ejesAlfabeticosB"
                      :items="getConfigOptions('ejesAlfabeticos')"
                      item-text="name"
                      item-value="id"
                      outlined
                      dense
                      :error="attemptedSubmit && !ejesValid"
                      :rules="requiredRule"
                      required
                      placeholder="Hasta"
                    />
                  </div>
                </div>

                <!-- Ejes Numéricos -->
                <div class="field-block">
                  <span class="row-label">Ejes Numéricos</span>
                  <div class="two-col">
                    <v-select
                      v-model="newEntregaForm.ejesNumericos"
                      :items="getConfigOptions('ejesNumericos')"
                      item-text="name"
                      item-value="id"
                      outlined
                      dense
                      :error="attemptedSubmit && !ejesValid"
                      :rules="requiredRule"
                      required
                      placeholder="Desde"
                    />
                    <v-select
                      v-model="newEntregaForm.ejesNumericosB"
                      :items="getConfigOptions('ejesNumericos')"
                      item-text="name"
                      item-value="id"
                      outlined
                      dense
                      :error="attemptedSubmit && !ejesValid"
                      :rules="requiredRule"
                      required
                      placeholder="Hasta"
                    />
                  </div>
                </div>

                <!-- Nivel -->
                <div v-if="ejesError" class="error--text" style="margin-top:6px">Completa los cuatro ejes</div>
                <div class="field-row" style="margin-bottom:0">
                  <span class="row-label">Nivel</span>
                  <v-select
                    v-model="newEntregaForm.nivel"
                    :items="getConfigOptions('niveles')"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    :rules="requiredRule"
                    required
                  />
                </div>
              </div>

              <!-- Especialidades -->
              <div class="form-section">
                <label class="field-label">Especialidades</label>
                <v-select
                  v-model="especialidades"
                  :items="$store.projectsStore.state.selectedProject.specialties"
                  item-text="name"
                  item-value="id"
                  placeholder="Seleccione especialidades"
                  outlined
                  dense
                  hide-details
                  multiple
                  :rules="requiredListRule"
                  required
                />
              </div>

              <!-- Supervisor -->
              <div class="form-section">
                <p class="section-title">Supervisor en Cargo</p>
                <v-autocomplete
                  v-model="newEntregaForm.supervisor_id"
                  :items="usersList"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  placeholder="Seleccionar supervisor"
                  prepend-inner-icon="mdi-account-tie"
                  :menu-props="{ maxHeight: '300px' }"
                  :rules="requiredRule"
                  required
                >
                  <template v-slot:selection="{ item }">
                    <div class="d-flex align-center">
                      <v-avatar size="20" class="mr-2" v-if="item.avatar">
                        <v-img :src="item.avatar" />
                      </v-avatar>
                      <v-icon size="20" class="mr-2" v-else>mdi-account</v-icon>
                      {{ item.name }}
                    </div>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar size="28">
                      <img v-if="item.avatar" :src="item.avatar" />
                      <v-icon v-else size="20">mdi-account</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>

                <!-- Supervisor card preview -->
                <div v-if="selectedSupervisor" class="supervisor-card mt-2">
                  <div class="s-avatar">
                    <img v-if="selectedSupervisor.avatar" :src="selectedSupervisor.avatar" />
                    <span v-else>{{ initials(selectedSupervisor.name) }}</span>
                  </div>
                  <div class="s-info">
                    <span class="s-name">{{ selectedSupervisor.name }}</span>
                    <span class="s-role">Supervisor de Obra</span>
                  </div>
                </div>
              </div>

              <!-- Asociaciones -->
              <div class="form-section">
                <p class="section-title">Asociaciones</p>
                <ItemsAssociationInputSelect :changeItems="changeAssociations"/>
              </div>

              <!-- Fecha Crítica -->
              <div class="form-section">
                <p class="section-title">Fecha Crítica</p>
                <v-menu
                  v-model="critical_date"
                  :close-on-content-click="false"
                  max-width="290"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="newEntregaForm.critical_date"
                      placeholder="Sin fecha"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      hide-details
                      clearable
                      style="max-width:240px"
                      @click:clear="newEntregaForm.critical_date = null"
                    />
                  </template>
                  <v-date-picker
                    locale="es"
                    v-model="newEntregaForm.critical_date"
                    @change="critical_date = false"
                  />
                </v-menu>
              </div>

              <!-- Archivos adjuntos -->
              <div class="form-section" style="border-bottom:none">
                <p class="section-title">Archivos adjuntos</p>
                <div class="attachments-row">
                  <div class="previews">
                    <div v-if="filesPreview.length === 0" class="files-empty">
                      <v-icon size="18" color="#D1D5DB">mdi-paperclip</v-icon>
                      <span>No hay archivos adjuntos</span>
                    </div>
                    <div v-for="(file, index) in filesPreview" :key="index" class="preview-item">
                      <div v-if="file.src" class="preview-img-wrap">
                        <button class="preview-remove" type="button" @click="removeAttachment(file, index)">
                          <v-icon size="10" color="white">mdi-close</v-icon>
                        </button>
                        <img :src="file.src" class="preview-thumb" />
                      </div>
                      <div v-else class="preview-file">
                        <v-icon size="14" color="#5E85EF">mdi-file-outline</v-icon>
                        <span>{{ file.name }}</span>
                        <button type="button" @click="removeAttachment(file, index)">
                          <v-icon size="12" color="#9CA3AF">mdi-close</v-icon>
                        </button>
                      </div>
                    </div>
                  </div>
                  <button class="attach-btn" type="button" @click="onButtonClick">
                    <v-icon size="14" color="#5E85EF">mdi-paperclip</v-icon>
                    Adjuntar
                  </button>
                  <input ref="uploader" multiple class="d-none" type="file" @change="onFilesChanged" />
                </div>
              </div>

            </div>

            <!-- Tab: Subtareas -->
            <div v-show="tab === 1">
              <div class="subtasks-tab">

                <div class="task-add-row">
                  <button class="btn-add-task" type="button" @click="addTask()">
                    <v-icon size="14" color="white" class="mr-1">mdi-note-plus</v-icon>
                    Agregar
                  </button>
                </div>

                <div v-if="newEntregaForm.tasks.length > 0">
                  <div class="tasks-header">
                    <div></div>
                    <div></div>
                    <span class="th-label">Título</span>
                    <span class="th-label">Descripción</span>
                  </div>

                  <draggable
                    v-model="newEntregaForm.tasks"
                    handle=".drag-handle"
                    :animation="200"
                    ghost-class="sortable-ghost"
                    chosen-class="sortable-chosen"
                  >
                    <div
                      v-for="(task, index) in newEntregaForm.tasks"
                      :key="index"
                      class="task-row"
                    >
                      <div class="drag-handle">
                        <v-icon size="18" color="#9CA3AF">mdi-drag</v-icon>
                      </div>
                      <div class="task-check">
                        <v-checkbox
                          v-model="task.checked"
                          dense
                          hide-details
                          color="#2D4B95"
                          class="ma-0 pa-0"
                        />
                      </div>
                      <v-text-field
                        v-model="task.grupo"
                        placeholder="Nombre de la tarea"
                        outlined
                        dense
                        hide-details
                        :rules="task.checked ? requiredRule : []"
                        class="task-input"
                      />
                      <v-text-field
                        v-model="task.title"
                        placeholder="Descripción"
                        outlined
                        dense
                        hide-details
                        :rules="task.checked ? requiredRule : []"
                        class="task-input"
                      />
                    </div>
                  </draggable>
                </div>

                <div v-else class="tasks-empty">
                  <v-icon size="32" color="#D1D5DB">mdi-format-list-checks</v-icon>
                  <span>No hay subtareas agregadas</span>
                </div>

              </div>
            </div>

          </v-form>
        </v-card-text>

        <!-- Footer -->
        <div class="modal-footer">
          <div class="footer-right">
            <button class="btn-cancel" type="button" @click="closeModalSelf()">Cancelar</button>
            <button v-if="checkPermissions(['create_entregas'])" class="btn-save" type="button" :disabled="loadingData" @click="saveEntrega()">
              <v-progress-circular
                v-if="loadingData"
                indeterminate
                color="white"
                :width="2"
                :size="14"
                class="mr-1"
              />
              Guardar
            </button>
            <button v-else class="btn-save" type="button" :disabled="loadingData" @click="saveDraftAndClose()">
              <v-progress-circular
                v-if="loadingData"
                indeterminate
                color="white"
                :width="2"
                :size="14"
                class="mr-1"
              />
              Guardar Borrador
            </button>
          </div>
        </div>

      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import _debounce from 'lodash/debounce'
import { create } from '@/helpers/api/entrega'
import { create as createDraftAPI, update as updateDraftAPI } from '@/helpers/api/drafts'
import { deleteFile } from '@/helpers/api/fileManager'
import EventBus from '@/helpers/event_bus'
import ControlPlanoModal from './ControlPlanoModal'
import draggable from 'vuedraggable'
import ItemsAssociationInputSelect from '@/components/Globals/Helpers/ItemsAssociationInputSelect'
import { getUsersList } from '@/helpers/api/user'

export default {
  name: 'CreateEntregaModal',
  components: {
    ControlPlanoModal,
    draggable,
    ItemsAssociationInputSelect
  },
  props: {
    modalStatus: {
      type: Boolean,
      default: false
    },
    closeModal: {
      type: Function,
      required: true
    },
    draftToLoad: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      validform: true,
      tab: 0,
      usersList: [],
      newEntregaForm: {
        title: '',
        protocolo: null,
        description: '',
        tasks: [],
        supervisor_id: null,
        critical_date: null,
        estimated_date: null,
        files: [],
        copy_to: null,
        rdi_ref: null,
        plano_ref: null,
        tareas_ref: null,
        adicionales_ref: null,
        plano_entrega: null,
        zona: null,
        ejesAlfabeticos: null,
        ejesAlfabeticosB: null,
        ejesNumericos: null,
        ejesNumericosB: null,
        nivel: null,
        plano_id: null,
        associations: [],
        reminder: [],
        reminder_repeatable: true
      },
      critical_date: false,
      requiredRule: [v => !!v || 'Campo requerido'],
      requiredListRule: [v => Array.isArray(v) && v.length > 0 || 'Campo requerido'],
      filesPreview: [],
      associations: [],
      especialidades: [],
      loadingData: false,
      ejesError: false,
      planoError: false,
      attemptedSubmit: false,
      draftId: null,
      draftCreated: false,
      draftUpdateInterval: 20,
      draftSaveTimer: null,
      handleFormChangeDebounced: null,
      isResetting: false
    }
  },
  computed: {
    protocolos() {
      const data = this.$store.entregaStore.state.listadoProtocolos.data || []
      return data.sort((a, b) => a.title.localeCompare(b.title))
    },
    planos() {
      return this.$store.entregaStore.state.planos.data || []
    },
    rdiList() {
      return this.$store.rdiStore.state.listadoRDI.data || []
    },
    tareasList() {
      return this.$store.consultasStore.state.listadoTareas.data || []
    },
    adicionalesList() {
      return this.$store.adicionalesStore.state.listado.data || []
    },
    projectId() {
      return this.$store.projectsStore.state.selectedProject.id
    },
    zonasList() {
      const config = this.$store.entregaStore.state.config || {}
      return (config.zonas || []).map(zona => ({
        id: zona.id,
        name: zona.name,
        niveles: zona.niveles || [],
        ejesAlfabeticos: zona.ejesAlfabeticos || [],
        ejesNumericos: zona.ejesNumericos || []
      }))
    },
    selectedSupervisor() {
      if (!this.newEntregaForm.supervisor_id) return null
      return this.usersList.find(u => u.id === this.newEntregaForm.supervisor_id) || null
    }
    ,
    ejesValid() {
      return [
        this.newEntregaForm.ejesAlfabeticos,
        this.newEntregaForm.ejesAlfabeticosB,
        this.newEntregaForm.ejesNumericos,
        this.newEntregaForm.ejesNumericosB
      ].every(value => value !== null && value !== undefined && value !== '')
    }
  },
  watch: {
    newEntregaForm: { handler() { this.handleFormChange() }, deep: true },
    associations: { handler() { this.handleFormChange() }, deep: true },
    especialidades: { handler() { this.handleFormChange() }, deep: true },
    draftToLoad(newVal) { if (newVal) this.loadDraft(newVal) },
    protocolos: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.newEntregaForm.protocolo) {
          this.newEntregaForm.protocolo = newVal[0].id
        }
      },
      deep: true
    },
    'newEntregaForm.protocolo': {
      handler(newVal) {
        if (!newVal) return
        const protocolo = this.protocolos.find(i => i.id === newVal)
        if (protocolo) {
          this.newEntregaForm.tasks = protocolo.tasks.map(i => ({
            checked: 1,
            id: i.id,
            title: i.title,
            grupo: i.grupo
          }))
        }
      },
      deep: true
    }
  },
  created() {
    const project_id = this.projectId
    this.$store.entregaStore.dispatch('getProtocolos', { project_id })
    this.$store.entregaStore.dispatch('getPlanos', { project_id })
    this.$store.rdiStore.dispatch('getRDI', { project_id })
    this.$store.consultasStore.dispatch('getMyTasks', { project_id, status: 1 })
    this.$store.adicionalesStore.dispatch('getAdditionals', { project_id, statuses: [1, 2] })
    this.$store.entregaStore.dispatch('getConfig', project_id)

    this.$nextTick(() => {
      if (this.protocolos.length > 0 && !this.newEntregaForm.protocolo) {
        this.newEntregaForm.protocolo = this.protocolos[0]?.id || null
      }
    })
  },
  async mounted() {
    this.handleFormChangeDebounced = _debounce(() => this.checkAndCreateDraft(), 1000)
    if (this.draftToLoad) this.loadDraft(this.draftToLoad)
    await this.loadUsers()
    if (this.$store.entregaStore.state.borradorEntrega) {
      const borrador = this.$store.entregaStore.state.borradorEntrega
      this.newEntregaForm = { ...this.newEntregaForm, ...borrador }
      if (borrador.associations) {
        try {
          this.associations = Array.isArray(borrador.associations) ? borrador.associations : JSON.parse(borrador.associations)
        } catch (e) {
          this.associations = borrador.associations || []
        }
      }
      if (borrador.especialidades) {
        try {
          this.especialidades = Array.isArray(borrador.especialidades) ? borrador.especialidades : JSON.parse(borrador.especialidades)
        } catch (e) {
          this.especialidades = []
        }
      }
      this.$store.consultasStore.commit('setBorradorEntrega', null)
    }
    this.fileListeners()
  },
  beforeUnmount() {
    if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
  },
  methods: {
    initials(name) {
      return (name || '').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('')
    },
    async loadUsers() {
      try {
        const projectId = this.$store.projectsStore.state.selectedProject.id
        const response = await getUsersList({ project_id: projectId })
        if (response.code === 200) {
          this.usersList = response.users
            .filter(user => user.projectjob.job_type_id == 6)
            .map(user => ({
              id: user.id,
              name: user.name,
              avatar: user.info?.avatar
            }))
        }
      } catch (error) {
        console.error('Error loading users:', error)
      }
    },
    getConfigOptions(type) {
      if (!this.$store.entregaStore.state.config || !this.$store.entregaStore.state.config[type]) {
        return []
      }
      return (this.$store.entregaStore.state.config[type] || []).map(item => ({
        id: item,
        name: item
      }))
    },
    handleZonaChange() {
      if (!this.newEntregaForm.zona) return
      const selectedZona = this.zonasList.find(zona => zona.id === this.newEntregaForm.zona)
      if (!selectedZona) return
      if (selectedZona.niveles?.length > 0) this.newEntregaForm.nivel = selectedZona.niveles[0]
      if (selectedZona.ejesAlfabeticos?.length > 0) this.newEntregaForm.ejesAlfabeticos = selectedZona.ejesAlfabeticos[0]
      if (selectedZona.ejesNumericos?.length > 0) this.newEntregaForm.ejesNumericos = selectedZona.ejesNumericos[0]
    },
    showErrorMessage(message) {
      this.$store.globalStore.dispatch('addMessage', {
        message,
        title: 'Error',
        created: new Date(),
        type: 'error',
        icon: 'mdi-alert-circle',
        color: '#fb8c00'
      })
    },
    showValidationErrors(error) {
      const response = error && error.data ? error.data : error
      const errors = response && response.errors ? response.errors : {}
      const messages = Object.keys(errors).reduce((result, field) => {
        const fieldErrors = Array.isArray(errors[field]) ? errors[field] : [errors[field]]
        return result.concat(fieldErrors.filter(Boolean))
      }, [])
      const message = messages[0] || (response && response.message)
      this.showErrorMessage(message || 'Error al guardar la entrega')
    },
    minimizeForm() {
      this.updateDraft()
      const title = 'Borrador Entrega: ' + (this.newEntregaForm.title || 'Sin Título')
      this.$store.globalStore.commit('addTaskToTasksHandler', {
        task_id: uuidv4(),
        title,
        type: 'Entrega',
        type_id: 9,
        number: null,
        draft_id: this.draftId
      })
      this.closeModalSelf()
    },
    addTask() {
      this.newEntregaForm.tasks.push({ checked: true, title: null, grupo: null })
    },
    handlePlanoSaved(planoData) {
			this.onFileChanged(planoData.screenshot, 16)
      this.newEntregaForm.plano_entrega = planoData || null
      this.planoError = false
    },
    changeAssociations(associations) { this.associations = associations.map(a => ({ type: a.type, id: a.id })) },
    closeModalSelf() {
      EventBus.$emit('reloadDraftList')
      if (this.handleFormChangeDebounced) this.handleFormChangeDebounced.cancel()
      this.updateDraft()
      this.isResetting = true
      if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
      this.resetForm()
      this.closeModal()
      this.$nextTick(() => setTimeout(() => { this.isResetting = false }, 100))
    },
    resetForm() {
      this.newEntregaForm = {
        title: '',
        protocolo: null,
        description: '',
        tasks: [],
        supervisor_id: null,
        critical_date: null,
        estimated_date: null,
        files: [],
        copy_to: null,
        rdi_ref: null,
        plano_ref: null,
        tareas_ref: null,
        adicionales_ref: null,
        plano_entrega: null,
        zona: null,
        ejesAlfabeticos: null,
        ejesAlfabeticosB: null,
        ejesNumericos: null,
        ejesNumericosB: null,
        nivel: null,
        plano_id: null,
        associations: [],
        reminder: [],
        reminder_repeatable: true
      }
      this.filesPreview = []
      this.associations = []
      this.especialidades = []
      this.tab = 0
      this.attemptedSubmit = false
      this.draftId = null
      this.draftCreated = false
    },
    onButtonClick() {
      this.$refs.uploader.click()
    },
    async onFilesChanged(e) {
			await this.createDraft();
      const files = e?.target?.files || []
      for (let i = 0; i < files.length; i++) {
        await this.onFileChanged(null, 13, files[i])
      }
    },
    async onFileChanged(e, type = 13, file = null) {
      const newFile = e?.target?.files[0] || file || (typeof File !== 'undefined' && e instanceof File ? e : null)
      if (!newFile) return
      if (!this.draftId && !this.draftCreated) await this.createDraft()
      const uuid = uuidv4()
      if (newFile.type.includes('image')) {
        const base64File = await this.toBase64(newFile)
        this.filesPreview.push({ src: base64File, name: newFile.name, uuid })
      } else {
        this.filesPreview.push({ noImage: true, name: newFile.name, uuid })
      }
      this.$store.globalStore.commit('setFilesToUploadCenter', {
        type,
        payload: { draft_id: this.draftId, project_id: this.projectId, uuid },
        description: this.newEntregaForm.title,
        total: newFile.size,
        uuid,
        file: newFile
      })
    },
    removeAttachment(file, index) {
      this.filesPreview.splice(index, 1)
      if (file && file.id) deleteFile({ file_id: file.id })
    },
    toBase64(file) {
      return new Promise(resolve => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => resolve(null)
      })
    },
    async getEntregaList() {
      const projectId = this.projectId || parseInt(localStorage.getItem('selectedProjectID'))
      if (projectId) {
        await this.$store.entregaStore.dispatch('getEntrega', { project_id: projectId })
      }
    },
    async saveEntrega() {
      try {
        this.loadingData = true
        this.newEntregaForm.project_id = this.projectId

        if (this.newEntregaForm.zona) {
          const selectedZona = this.zonasList.find(z => z.id === this.newEntregaForm.zona)
          if (selectedZona) this.newEntregaForm.zona_name = selectedZona.name
        }

        // Reset inline validation flags
        this.ejesError = false
        this.planoError = false
        this.attemptedSubmit = true

        // Validate form fields (Vuetify rules)
        if (!this.$refs.form.validate()) {
          this.showErrorMessage('Complete los campos obligatorios')
          this.loadingData = false
          return
        }

        // Validate all axis range values required by the creation contract
        if (!this.ejesValid) {
          this.ejesError = true
          this.showErrorMessage('Completa los cuatro ejes')
          this.loadingData = false
          return
        }

        // The API requires a persisted blueprint identifier
        if (
          !this.newEntregaForm.plano_entrega ||
          !this.newEntregaForm.plano_entrega.plano_id
        ) {
          this.planoError = true
          this.showErrorMessage('El emplazamiento (plano) es obligatorio')
          this.loadingData = false
          return
        }

        if (!Array.isArray(this.especialidades) || this.especialidades.length === 0) {
          this.showErrorMessage('Selecciona al menos una especialidad')
          this.loadingData = false
          return
        }

        const protocolo = this.protocolos.find(i => i.id === this.newEntregaForm.protocolo)
        const entregaData = {
          ...this.newEntregaForm,
          protocolo_name: protocolo?.title,
          tasks: this.cleanTasks(this.newEntregaForm.tasks),
          associations: JSON.stringify(this.associations),
          draft_id: this.draftId || undefined
        }
        entregaData.especialidades = JSON.stringify(this.especialidades)
        const response = await create(entregaData)

        if (response.code === 200) {
          if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
          await this.getEntregaList()
          this.closeModalSelf()
          this.setMessage(response.rdi)
          if (response.task) this.checkReloadTask(response.task)
        }
      } catch (error) {
        console.error('Error saving entrega:', error)
        this.showValidationErrors(error)
      } finally {
        this.loadingData = false
      }
    },
    checkReloadTask(task) {
      const currentTarea = this.$store.consultasStore.state.currentTarea
      if (currentTarea && currentTarea.id === task.id) {
        this.$store.consultasStore.commit('setCurrentTarea', task)
      }
    },
    setMessage(entrega) {
      this.$store.globalStore.dispatch('addMessage', {
        message: 'Haz click aquí para visualizarla.',
        title: 'Se ha creado la Entrega ' + entrega.number,
        created: new Date(),
        type: 'success',
        icon: 'mdi-check-circle',
        color: '#3EBFA3',
        action: 'entrega',
        entrega_id: entrega.id
      })
    },

    cleanTasks(tasks) {
      if (!Array.isArray(tasks)) return []
      return tasks
        .filter(t => t && (t.checked === true || t.checked == 1 || t.checked === '1'))
        .map(t => {
          const grupo = (t.grupo || '').toString().trim()
          const title = (t.title || '').toString().trim()
          return {
            ...t,
            grupo: grupo === '' ? null : grupo,
            title: title === '' ? null : title,
            checked: 1
          }
        })
        .filter(t => (t.grupo !== null && t.grupo !== '') || (t.title !== null && t.title !== ''))
    },

    // ── DRAFT SYSTEM ──
    handleFormChange() {
      if (!this.isResetting && this.handleFormChangeDebounced) this.handleFormChangeDebounced()
    },
    checkAndCreateDraft() {
      if (!this.draftCreated && !this.draftId) this.createDraft()
    },
    async createDraft() {
      if (this.draftCreated) return
      this.draftCreated = true
      const draftData = {
        type: 9,
        project_id: this.projectId,
        data: JSON.stringify({ form: { ...this.newEntregaForm, tasks: this.cleanTasks(this.newEntregaForm.tasks) }, especialidades: this.especialidades, associations: this.associations })
      }
      try {
        const response = await createDraftAPI(draftData)
        if (response.code === 200) {
          this.draftId = response.draft.id
          this.startDraftUpdateTimer()
          return this.draftId
        }
        else if(response.offline) {
					this.$store.globalStore.dispatch('addMessage', {
						message: 'No hay conexión. El borrador se mantendrá localmente y podrás retomarlo cuando vuelvas a tener conexión desde la sección de borradores.',
						title: 'Borrador guardado localmente',
						created: new Date(),
						type: 'warning',
						icon: 'mdi-wifi-off',
						color: '#F59E0B'
					})
					this.draftId = response.draft.id
					this.startDraftUpdateTimer()
					return this.draftId
        }
      } catch (error) {
        console.error('Error al crear borrador:', error)
        this.draftCreated = false
        throw error
      }
    },
    startDraftUpdateTimer() {
      if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
      this.draftSaveTimer = setInterval(() => this.updateDraft(), this.draftUpdateInterval * 1000)
    },
    async updateDraft() {
      if (!this.draftId) return
      try {
        await updateDraftAPI({
          id: this.draftId,
          type: 9,
          data: JSON.stringify({ form: { ...this.newEntregaForm, tasks: this.cleanTasks(this.newEntregaForm.tasks) }, especialidades: this.especialidades, associations: this.associations })
        })
      } catch (error) {
        console.error('Error al actualizar borrador:', error)
      }
    },
    async loadDraft(draft) {
      if (!draft) return
      this.isResetting = true
      if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
      let draftData = draft.data
      if (typeof draftData === 'string') {
        try { draftData = JSON.parse(draftData) } catch (e) { this.isResetting = false; return }
      }
      if (draftData.form) this.newEntregaForm = { ...this.newEntregaForm, ...draftData.form }
      if (draftData.especialidades && Array.isArray(draftData.especialidades)) this.especialidades = [...draftData.especialidades]
      if (draftData.associations) this.associations = [...draftData.associations]
      try {
        const filePreviews = []
        if (draft.files && Array.isArray(draft.files)) {
          for (const file of draft.files) {
            if (file.mime_type && file.mime_type.includes('image')) {
              filePreviews.push(file.pathmedia?.path ? { src: file.pathmedia.path, id: file.id, name: file.name } : { noImage: true, id: file.id, name: file.name })
            } else {
              filePreviews.push({ noImage: true, id: file.id, name: file.name })
            }
          }
        }
        this.filesPreview = filePreviews
      } catch (e) {
        console.error('Error al cargar archivos del draft:', e)
      }
      this.draftId = draft.id
      this.draftCreated = true
      this.startDraftUpdateTimer()
      this.$nextTick(() => { this.isResetting = false })
      this.$store.globalStore.dispatch('addMessage', {
        message: 'Borrador cargado correctamente.',
        title: 'Borrador Cargado',
        created: new Date(),
        type: 'success',
        icon: 'mdi-check-circle',
        color: '#3EBFA3'
      })
      this.$store.entregaStore.commit('setDraftToLoad', null)
    },
    async saveDraftAndClose() {
      try {
        this.loadingData = true
        await this.updateDraft()
        this.$store.globalStore.dispatch('addMessage', {
          message: 'Borrador guardado correctamente.',
          title: 'Borrador Guardado',
          type: 'success',
          icon: 'mdi-check-circle',
          color: '#3EBFA3',
          created: new Date()
        })
        this.closeModalSelf()
      } catch(error) {
        console.error('Error al guardar borrador:', error)
        this.$store.globalStore.dispatch('addMessage', {
          message: 'Error al guardar el borrador. Por favor, intenta de nuevo.',
          title: 'Error',
          type: 'error',
          icon: 'mdi-alert-circle',
          color: '#fb8c00',
          created: new Date()
        })
      } finally {
        this.loadingData = false
      }
    },
    fileListeners() {
      EventBus.$on('completedFileUpload', (payload) => {
        if (payload && payload.draft_id === this.draftId) {
          const findIndex = this.filesPreview.findIndex(f => f.uuid === payload.uuid)
          if (findIndex !== -1) this.$set(this.filesPreview[findIndex], 'id', payload.file.id)
        }
      })
    }
  }
}
</script>

<style scoped>
/* ── Modal Card ── */
.modal-card {
  border-radius: 12px !important;
  overflow: hidden;
}

/* ── Header ── */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #2D4B95;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.modal-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.modal-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.modal-title {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: white;
  line-height: 1.2;
}
.modal-subtitle {
  margin: 0;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.2;
  margin-top: 2px;
}
.modal-subtitle .separator {
  margin: 0 5px;
  opacity: 0.5;
}
.modal-header-actions {
  display: flex;
  gap: 6px;
}
.header-btn {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.header-btn.minimize { background: rgba(251, 140, 0, 0.9); }
.header-btn.minimize:hover { background: rgba(251, 140, 0, 1); }
.header-btn.close { background: rgba(244, 81, 108, 0.9); }
.header-btn.close:hover { background: rgba(244, 81, 108, 1); }

/* ── Body ── */
.modal-body {
  overflow-y: auto;
  max-height: calc(90vh - 80px);
}

/* ── Form Sections ── */
.form-section {
  padding: 14px 20px;
  border-bottom: 1px solid #F3F4F6;
}

/* ── Title Input ── */
.title-section {
  padding: 14px 20px;
}
/deep/ .title-input .v-input__control .v-input__slot {
  min-height: 40px !important;
}
/deep/ .title-input input {
  font-size: 14px !important;
  font-weight: 500;
}

/* ── Field Label ── */
.field-label {
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: #2D4B95;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}

/* ── Section Title ── */
.section-title {
  font-size: 11px;
  font-weight: 500;
  color: #2D4B95;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}

/* ── Field Rows ── */
.field-row {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
}
.field-block {
  margin-bottom: 8px;
}
.field-block:last-child {
  margin-bottom: 0;
}
.row-label {
  font-size: 12px;
  font-weight: 500;
  color: #2D4B95;
  display: flex;
  align-items: center;
}
.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 6px;
}

/* ── Plano ── */
.plano-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.plano-hint {
  font-size: 12px;
  color: #9CA3AF;
}

/* ── Supervisor Card ── */
.supervisor-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #F9FAFB;
  border: 0.5px solid #E5E7EB;
  border-radius: 8px;
}
.s-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #E0E7FF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  color: #4F46E5;
  flex-shrink: 0;
  overflow: hidden;
}
.s-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.s-info {
  display: flex;
  flex-direction: column;
}
.s-name {
  font-size: 12px;
  font-weight: 500;
  color: #111827;
}
.s-role {
  font-size: 11px;
  color: #6B7280;
}

/* ── Attachments ── */
.attachments-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.previews { display: flex; gap: 6px; flex-wrap: wrap; flex: 1; }
.files-empty {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 0;
  font-size: 12px;
  color: #9CA3AF;
}
.preview-img-wrap { position: relative; }
.preview-thumb { width: 40px; height: 40px; border-radius: 6px; object-fit: cover; display: block; }
.preview-remove {
  position: absolute;
  top: -4px; right: -4px;
  width: 16px; height: 16px;
  border-radius: 50%;
  background: #F4516C;
  border: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.preview-file {
  display: flex; align-items: center; gap: 4px;
  padding: 3px 8px;
  border-radius: 5px;
  background: #EEF2FF;
  font-size: 11px;
  color: #4B5563;
}
.preview-file button { background: none; border: none; cursor: pointer; display: flex; align-items: center; }
.attach-btn {
  display: flex; align-items: center; gap: 5px;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1.5px solid #E5E7EB;
  background: white;
  cursor: pointer;
  font-size: 12px;
  color: #5E85EF;
  font-weight: 500;
  transition: border-color 0.15s;
  white-space: nowrap;
}
.attach-btn:hover { border-color: #5E85EF; }

/* ── Tabs ── */
.tabs-bar {
  display: flex;
  border-bottom: 1px solid #F3F4F6;
  padding: 0 20px;
}
.tab-btn {
  padding: 10px 0;
  margin-right: 24px;
  font-size: 12px;
  font-weight: 500;
  color: #6B7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: all 0.15s;
  margin-bottom: -1px;
}
.tab-btn.active {
  color: #2D4B95;
  border-bottom-color: #2D4B95;
}

/* ── Subtasks Tab ── */
.subtasks-tab {
  padding: 0 20px 16px;
}
.task-add-row {
  display: flex;
  justify-content: flex-end;
  margin: 14px 0 10px;
}
.btn-add-task {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 7px;
  background: #2D4B95;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: white;
  transition: background 0.15s;
}
.btn-add-task:hover {
  background: #253d7a;
}
.tasks-header {
  display: grid;
  grid-template-columns: 32px 44px 1fr 1fr;
  gap: 8px;
  align-items: center;
  padding: 6px 10px;
  background: #F9FAFB;
  border-radius: 6px;
  margin-bottom: 4px;
}
.th-label {
  font-size: 11px;
  font-weight: 500;
  color: #2D4B95;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.task-row {
  display: grid;
  grid-template-columns: 32px 44px 1fr 1fr;
  gap: 8px;
  align-items: center;
  padding: 6px 10px;
  border-bottom: 0.5px solid #F3F4F6;
}
.task-row:last-child {
  border-bottom: none;
}
.drag-handle {
  cursor: grab;
  display: flex;
  align-items: center;
}
.task-check {
  display: flex;
  align-items: center;
  justify-content: center;
}
/deep/ .task-input .v-input__slot {
  min-height: 34px !important;
}
/deep/ .task-input input {
  font-size: 12px !important;
}
.tasks-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background: #F9FAFB;
  border-radius: 8px;
  gap: 6px;
  margin-top: 8px;
}
.tasks-empty span {
  font-size: 12px;
  color: #9CA3AF;
}
.sortable-ghost { opacity: 0.4; background: #F0F4FF; }
.sortable-chosen { background: #F9FAFB; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }

/* ── Footer ── */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 20px;
  background: #F9FAFB;
  border-top: 1px solid #E5E7EB;
}
.btn-cancel {
  padding: 7px 16px;
  border-radius: 7px;
  border: 1.5px solid #E5E7EB;
  background: white;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  transition: all 0.15s;
}
.btn-cancel:hover {
  background: #F3F4F6;
}
.footer-right {
  display: flex;
  gap: 8px;
  align-items: center;
}
.btn-save {
  display: flex;
  align-items: center;
  padding: 7px 20px;
  border-radius: 7px;
  border: none;
  background: #2D4B95;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: white;
  transition: background 0.15s;
}
.btn-save:hover {
  background: #253d7a;
}
.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Vuetify overrides ── */
/deep/ .v-text-field--outlined fieldset {
  border-color: #E5E7EB !important;
}
/deep/ .v-text-field--outlined:hover fieldset {
  border-color: #D1D5DB !important;
}
/deep/ .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #2D4B95 !important;
}
/deep/ .remove-bottom-details .v-text-field__details { display: none; }
/deep/ .remove-bottom-details .v-input__slot { margin-bottom: 0; }

/* ── Mobile ── */
@media (max-width: 600px) {
  .field-row {
    grid-template-columns: 1fr;
  }
  .two-col {
    grid-template-columns: 1fr;
  }
  .tasks-header,
  .task-row {
    grid-template-columns: 28px 36px 1fr 1fr;
    gap: 5px;
  }
}
</style>